import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import {
    Box,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    SextforceAutoCampaignTipReply,
    SextforceAutoCampaignTipReplyMessageDetails,
    SextforceAutoCampaignTipReplyWithMessageDetails,
} from '../../../../hooks/useSextforceAutoCampaignTipReply';
import { SettingsContext } from '../../../../store/SettingsContext';
import { UserContext } from '../../../../store/UserContext';
import { handleHttpError } from '../../../../utils/common';
import SextforceAutoCampaignTipReplyEdit from './SextforceAutoCampaignTipReplyEditDialog';
import SextforceAutoCampaignTipReplyTableTipRow from './SextforceAutoCampaignTipReplyTableTipRow';
import SextforceAutoCampaignTipReplyTippersListDialog from './SextforceAutoCampaignTipReplyTippersListDialog';

type Props = {
    subscriber: any;
    autoCampaignTipReply: SextforceAutoCampaignTipReplyWithMessageDetails;
    theme: Theme;
    refetchAutoCampaignTipReplies: () => void;
    updateAutoCampaignTipReply: (
        id: string,
        autoCampaignReply: SextforceAutoCampaignTipReply,
    ) => Promise<SextforceAutoCampaignTipReply | null>;
    deleteAutoCampaignTipReply: (id: string) => Promise<SextforceAutoCampaignTipReply | null>;
    createAutoCampaignTipReplyTipMessageDetails: (
        autoCampaignTipReply: SextforceAutoCampaignTipReply,
        autoCampaignTipReplyTipMessageDetails: SextforceAutoCampaignTipReplyMessageDetails,
    ) => Promise<SextforceAutoCampaignTipReplyMessageDetails | null>;
    updateAutoCampaignTipReplyTipMessageDetails: (
        autoCampaignTipReply: SextforceAutoCampaignTipReply,
        autoCampaignTipReplyTipMessageDetails: SextforceAutoCampaignTipReplyMessageDetails,
    ) => Promise<SextforceAutoCampaignTipReplyMessageDetails | null>;
    deleteAutoCampaignTipReplyTipMessageDetails: (
        autoCampaignTipReply: SextforceAutoCampaignTipReply,
        autoCampaignTipReplyTipMessageDetails: SextforceAutoCampaignTipReplyMessageDetails,
    ) => Promise<SextforceAutoCampaignTipReplyMessageDetails | null>;
};

const SextforceAutoCampaignTipReplyTableRow = (props: Props) => {
    const {
        subscriber,
        autoCampaignTipReply,
        theme,
        refetchAutoCampaignTipReplies,
        updateAutoCampaignTipReply,
        deleteAutoCampaignTipReply,
        createAutoCampaignTipReplyTipMessageDetails,
        updateAutoCampaignTipReplyTipMessageDetails,
        deleteAutoCampaignTipReplyTipMessageDetails,
    } = props;
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const dialog = useDialog();
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [tippersListDialogOpen, setTippersListDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async (id: string) => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return;
        }

        setIsDeleting(true);

        const data = await deleteAutoCampaignTipReply(id)
            .then(async response => {
                setIsDeleting(false);

                return response;
            })
            .catch(error => {
                setIsDeleting(false);
                handleHttpError(error, dialog);

                return null;
            });

        return data;
    };

    const handleDeleteAutoCampaignTipReply = async (autoRepostId: string) => {
        handleDelete(autoRepostId).then(data => {
            if (!data) {
                return;
            }

            refetchAutoCampaignTipReplies();
        });
    };

    const closeMenu = () => setMenuAnchorEl(null);

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
            }}
        >
            <Stack direction="column" spacing={0.5} sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <LinkIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                    <Stack direction="column" spacing={0} sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Link
                            to={autoCampaignTipReply.campaignUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => {
                                e.stopPropagation();
                            }}
                        >
                            <Typography variant="subtitle2" color="textPrimary" noWrap>
                                {autoCampaignTipReply.name}
                            </Typography>
                        </Link>
                        {autoCampaignTipReply.description && (
                            <Typography variant="caption" color="textSecondary" noWrap>
                                {autoCampaignTipReply.description}
                            </Typography>
                        )}
                    </Stack>
                    <Stack direction="column" spacing={0} alignItems="center" sx={{ flexShrink: 0, minWidth: 48 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                            {(autoCampaignTipReply.replyCount || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Replies
                        </Typography>
                    </Stack>
                    <IconButton
                        size="small"
                        sx={{ flexShrink: 0 }}
                        onClick={e => {
                            e.stopPropagation();
                            setMenuAnchorEl(e.currentTarget);
                        }}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="textSecondary" noWrap>
                        Posted: {moment(autoCampaignTipReply.postDate).format('L hh:mma')}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" noWrap>
                        Expires:{' '}
                        {autoCampaignTipReply.postExpireDate ? moment(autoCampaignTipReply.postExpireDate).format('L hh:mma') : 'N/A'}
                    </Typography>
                </Stack>
            </Stack>

            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
                <MenuItem
                    disabled={!autoCampaignTipReply.active}
                    onClick={() => {
                        closeMenu();
                        setEditDialogOpen(true);
                    }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit Campaign Settings</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        closeMenu();
                        setTippersListDialogOpen(true);
                    }}
                >
                    <ListItemIcon>
                        <PeopleAltIcon fontSize="small" color="info" />
                    </ListItemIcon>
                    <ListItemText>List of All Tippers</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={!autoCampaignTipReply.active || isDeleting}
                    onClick={() => {
                        closeMenu();

                        dialog
                            .confirm({
                                title: 'Delete Auto Tip Campaign',
                                message: 'Are you sure you want to delete this Auto Tip Campaign?',
                                cancel: {
                                    text: 'Cancel',
                                    variant: 'outlined',
                                },
                                ok: {
                                    text: 'Delete',
                                    variant: 'contained',
                                    color: 'error',
                                },
                            })
                            .then(() => {
                                if (autoCampaignTipReply._id) {
                                    handleDeleteAutoCampaignTipReply(autoCampaignTipReply._id);
                                }
                            })
                            .catch(() => {});
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText sx={{ color: theme.palette.error.main }}>Delete Campaign</ListItemText>
                </MenuItem>
            </Menu>

            <Table
                size="small"
                sx={{
                    '& td, & th': {
                        paddingLeft: 1.5,
                        paddingRight: 1.5,
                    },
                    // Remove the border from the cells in the last row
                    '& tr:last-child td': {
                        borderBottom: 'none',
                    },
                }}
            >
                <TableHead>
                    <TableRow
                        sx={{
                            backgroundColor: theme.palette.grey[100],
                            '& th': {
                                fontWeight: 'bold',
                                borderBottom: 'none',
                            },
                        }}
                    >
                        <TableCell>Tip Amount</TableCell>
                        <TableCell width={70} align="center">
                            Replies
                        </TableCell>
                        <TableCell width={100} align="center">
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {autoCampaignTipReply.tipsOptions.map((tipOption: number, index: number) => (
                        <SextforceAutoCampaignTipReplyTableTipRow
                            subscriber={subscriber}
                            autoCampaignTipReply={autoCampaignTipReply}
                            tipOption={tipOption}
                            theme={theme}
                            refetchAutoCampaignTipReplies={refetchAutoCampaignTipReplies}
                            createAutoCampaignTipReplyTipMessageDetails={createAutoCampaignTipReplyTipMessageDetails}
                            updateAutoCampaignTipReplyTipMessageDetails={updateAutoCampaignTipReplyTipMessageDetails}
                            deleteAutoCampaignTipReplyTipMessageDetails={deleteAutoCampaignTipReplyTipMessageDetails}
                            key={index}
                        />
                    ))}
                </TableBody>
            </Table>

            {tippersListDialogOpen && (
                <SextforceAutoCampaignTipReplyTippersListDialog
                    subscriber={subscriber}
                    autoCampaignTipReply={autoCampaignTipReply}
                    messageDetailsList={autoCampaignTipReply.messageDetails}
                    amount={null}
                    open={tippersListDialogOpen}
                    onClose={() => setTippersListDialogOpen(false)}
                    theme={theme}
                />
            )}

            {editDialogOpen && (
                <SextforceAutoCampaignTipReplyEdit
                    subscriber={subscriber}
                    autoCampaignTipReply={autoCampaignTipReply}
                    updateAutoCampaignTipReply={updateAutoCampaignTipReply}
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    refetchAutoCampaignTipReply={refetchAutoCampaignTipReplies}
                    theme={theme}
                />
            )}
        </Box>
    );
};

export default SextforceAutoCampaignTipReplyTableRow;
